import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  AlertTriangle, Loader2, ArrowUp, ArrowDown, Sparkles, MessageSquare, RefreshCw, Zap,
} from "lucide-react";

const PRESETS = [
  { amount: 3000, label: "₹3,000", note: "~10,000 SMS OTPs" },
  { amount: 5000, label: "₹5,000", note: "~16,000 OTPs or ~1,600 Try-on images" },
  { amount: 10000, label: "₹10,000", note: "Best value — most stores last 1-3 months" },
];

const SERVICE_ICON = {
  ai: Sparkles,
  messaging: MessageSquare,
  other: RefreshCw,
};

export default function AdminWallet() {
  const [wallet, setWallet] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [txns, setTxns] = useState([]);
  const [autoCfg, setAutoCfg] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [custom, setCustom] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    const [w, b, t, a] = await Promise.all([
      api.get("/admin/wallet").then(r => r.data),
      api.get("/admin/wallet/breakdown").then(r => r.data),
      api.get("/admin/wallet/transactions?limit=20").then(r => r.data),
      api.get("/admin/wallet/auto-recharge").then(r => r.data),
    ]);
    setWallet(w); setBreakdown(b); setTxns(t.items || []); setAutoCfg(a);
  };

  useEffect(() => { refresh().catch(() => toast.error("Couldn't load wallet")); }, []);

  const recharge = async (amount) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/wallet/recharge", { amount });
      toast.success(`Wallet credited with ₹${amount}. New balance: ₹${data.balance.toFixed(2)}`);
      setCustom("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recharge failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!wallet) return <div className="h-32 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />;

  const isLow = wallet.is_low;

  return (
    <div data-testid="admin-wallet-page" className="space-y-6 max-w-5xl">
      <div>
        <div className="overline mb-1">Billing</div>
        <h1 className="font-display text-3xl md:text-4xl">Wallet</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-2xl">
          Your prepaid balance funds customer OTP delivery and AI features (Try-on, Smart Catalog).
          Recharge when low so customer login keeps working.
        </p>
      </div>

      {/* Balance hero card */}
      <section className={`p-6 border-2 ${isLow ? "border-amber-400 bg-amber-50" : "border-[hsl(var(--kb-border))] bg-white"}`} data-testid="wallet-balance-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))] mb-1">Current balance</div>
            <div className="font-display text-5xl">₹{wallet.balance.toFixed(2)}</div>
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-2">
              Low-balance warning at ₹{wallet.low_balance_threshold}
            </div>
          </div>
          {isLow && (
            <div className="bg-amber-100 text-amber-900 px-3 py-2 text-xs inline-flex items-center gap-2" data-testid="wallet-low-banner">
              <AlertTriangle size={14} /> Low balance — recharge to avoid OTP failures
            </div>
          )}
        </div>
      </section>

      {/* Recharge */}
      <section className="bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="wallet-recharge-section">
        <h2 className="font-display text-xl mb-1">Recharge</h2>
        <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
          Pick a preset or enter a custom amount. (Razorpay payment integration ships with the next release —
          today this is a goodwill credit by super-admin on request.)
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          {PRESETS.map((p) => (
            <button
              key={p.amount}
              type="button"
              disabled={submitting}
              onClick={() => recharge(p.amount)}
              className="border border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))] p-4 text-left transition disabled:opacity-50"
              data-testid={`recharge-${p.amount}`}
            >
              <div className="font-display text-2xl mb-1">{p.label}</div>
              <div className="text-xs text-[hsl(var(--kb-muted))]">{p.note}</div>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            className="kb-input flex-1"
            type="number"
            placeholder="Custom amount in ₹ (min 100)"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            min={100}
            data-testid="recharge-custom-input"
          />
          <button
            type="button"
            disabled={submitting || !custom || parseFloat(custom) < 100}
            onClick={() => recharge(parseFloat(custom))}
            className="kb-btn-primary"
            data-testid="recharge-custom-btn"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : "Recharge"}
          </button>
        </div>
      </section>

      {/* Auto-recharge */}
      {autoCfg && (
        <section className="bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="auto-recharge-section">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="font-display text-xl flex items-center gap-2">
                <Zap size={18} /> Auto-recharge
              </h2>
              <p className="text-sm text-[hsl(var(--kb-muted))] mt-1 max-w-xl">
                Top up automatically before OTPs start failing. We&apos;ll charge your saved method whenever
                the balance dips below your threshold — with a 5-minute cooldown and a daily safety cap.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <span className="text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))]">
                {autoCfg.enabled ? "On" : "Off"}
              </span>
              <input
                type="checkbox"
                className="sr-only peer"
                checked={!!autoCfg.enabled}
                onChange={(e) => setAutoCfg({ ...autoCfg, enabled: e.target.checked })}
                data-testid="auto-recharge-toggle"
              />
              <span className="w-10 h-5 bg-[hsl(var(--kb-border))] rounded-full relative transition-colors peer-checked:bg-[hsl(var(--kb-primary))]">
                <span className={`absolute top-0.5 ${autoCfg.enabled ? "left-5" : "left-0.5"} w-4 h-4 bg-white rounded-full transition-all`} />
              </span>
            </label>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-[hsl(var(--kb-muted))]">When balance falls below</span>
              <div className="flex items-center mt-1">
                <span className="px-2 py-2 border border-r-0 border-[hsl(var(--kb-border))] text-sm text-[hsl(var(--kb-muted))]">₹</span>
                <input
                  className="kb-input flex-1"
                  type="number"
                  min={autoCfg.min_threshold || 100}
                  value={autoCfg.threshold}
                  onChange={(e) => setAutoCfg({ ...autoCfg, threshold: parseFloat(e.target.value) || 0 })}
                  data-testid="auto-recharge-threshold"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs text-[hsl(var(--kb-muted))]">Recharge by</span>
              <div className="flex items-center mt-1">
                <span className="px-2 py-2 border border-r-0 border-[hsl(var(--kb-border))] text-sm text-[hsl(var(--kb-muted))]">₹</span>
                <input
                  className="kb-input flex-1"
                  type="number"
                  min={autoCfg.min_recharge_amount || 500}
                  value={autoCfg.recharge_amount}
                  onChange={(e) => setAutoCfg({ ...autoCfg, recharge_amount: parseFloat(e.target.value) || 0 })}
                  data-testid="auto-recharge-amount"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs text-[hsl(var(--kb-muted))]">Daily safety cap</span>
              <div className="flex items-center mt-1">
                <span className="px-2 py-2 border border-r-0 border-[hsl(var(--kb-border))] text-sm text-[hsl(var(--kb-muted))]">₹</span>
                <input
                  className="kb-input flex-1"
                  type="number"
                  min={autoCfg.recharge_amount || 500}
                  value={autoCfg.daily_cap}
                  onChange={(e) => setAutoCfg({ ...autoCfg, daily_cap: parseFloat(e.target.value) || 0 })}
                  data-testid="auto-recharge-cap"
                />
              </div>
            </label>
          </div>

          <div className="mt-3">
            <label className="block">
              <span className="text-xs text-[hsl(var(--kb-muted))]">Saved payment method</span>
              <input
                className="kb-input w-full mt-1"
                placeholder="e.g. HDFC Credit •••• 4242  (will use saved Razorpay token when ready)"
                value={autoCfg.payment_method_label || ""}
                onChange={(e) => setAutoCfg({ ...autoCfg, payment_method_label: e.target.value })}
                data-testid="auto-recharge-method"
              />
              <span className="text-[10px] text-[hsl(var(--kb-muted))] mt-1 inline-block">
                Razorpay saved-method tokens are wired in the next release — for now this label is just a confirmation that you&apos;ve thought through the auth flow.
              </span>
            </label>
          </div>

          {autoCfg.last_status && (
            <div className={`mt-3 p-2 text-xs ${
              autoCfg.last_status === "ok"
                ? "bg-green-50 text-green-800"
                : autoCfg.last_status === "cap_reached"
                ? "bg-amber-50 text-amber-900"
                : "bg-red-50 text-red-800"
            }`} data-testid="auto-recharge-last-status">
              Last attempt: <span className="font-mono">{autoCfg.last_status}</span>
              {autoCfg.last_triggered_at && (
                <span className="opacity-70 ml-2">{new Date(autoCfg.last_triggered_at).toLocaleString()}</span>
              )}
              {autoCfg.last_error && <div className="mt-1">{autoCfg.last_error}</div>}
            </div>
          )}

          <div className="mt-4">
            <button
              type="button"
              disabled={autoSaving}
              onClick={async () => {
                setAutoSaving(true);
                try {
                  const { data } = await api.put("/admin/wallet/auto-recharge", {
                    enabled: !!autoCfg.enabled,
                    threshold: autoCfg.threshold,
                    recharge_amount: autoCfg.recharge_amount,
                    daily_cap: autoCfg.daily_cap,
                    payment_method_label: autoCfg.payment_method_label || null,
                  });
                  setAutoCfg(data);
                  toast.success(data.enabled ? "Auto-recharge is ON" : "Auto-recharge settings saved");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Save failed");
                } finally { setAutoSaving(false); }
              }}
              className="kb-btn-primary"
              data-testid="auto-recharge-save"
            >
              {autoSaving ? <Loader2 size={14} className="animate-spin" /> : "Save auto-recharge"}
            </button>
          </div>
        </section>
      )}

      {/* MTD breakdown */}
      <section className="bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="wallet-breakdown">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-display text-xl">Where your money went this month</h2>
            <p className="text-xs text-[hsl(var(--kb-muted))]">
              Spent ₹{breakdown?.mtd_spend?.toFixed(2) || "0.00"} · Recharged ₹{breakdown?.mtd_recharge?.toFixed(2) || "0.00"}
            </p>
          </div>
          <button type="button" onClick={refresh} className="text-xs underline text-[hsl(var(--kb-muted))]" data-testid="wallet-refresh">
            <RefreshCw size={12} className="inline mr-1" /> Refresh
          </button>
        </div>
        {!breakdown?.by_service?.length ? (
          <div className="text-sm text-[hsl(var(--kb-muted))]">No usage this month yet.</div>
        ) : (
          <ul className="divide-y divide-[hsl(var(--kb-border))]">
            {breakdown.by_service.map((s) => {
              const Icon = SERVICE_ICON[s.category] || RefreshCw;
              return (
                <li key={s.service_key} className="py-3 flex items-center gap-3" data-testid={`breakdown-${s.service_key}`}>
                  <div className="w-10 h-10 bg-[hsl(var(--kb-surface-alt))] flex items-center justify-center">
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{s.label}</div>
                    <div className="text-xs text-[hsl(var(--kb-muted))]">
                      {s.events} event(s) · ₹{s.unit_price.toFixed(2)} {s.unit}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg">₹{s.total_amount.toFixed(2)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Recent transactions */}
      <section className="bg-white border border-[hsl(var(--kb-border))]" data-testid="wallet-transactions">
        <div className="px-5 py-3 border-b border-[hsl(var(--kb-border))] font-medium">Recent transactions</div>
        {!txns.length ? (
          <div className="p-6 text-sm text-[hsl(var(--kb-muted))]">No transactions yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[hsl(var(--kb-surface-alt))]">
              <tr>
                <th className="p-3 text-left">When</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Detail</th>
                <th className="p-3 text-right">Amount</th>
                <th className="p-3 text-right">Balance after</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`txn-${t.id}`}>
                  <td className="p-3">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    {t.type === "credit" ? (
                      <span className="text-green-700 inline-flex items-center gap-1"><ArrowUp size={10} /> Credit</span>
                    ) : (
                      <span className="text-red-700 inline-flex items-center gap-1"><ArrowDown size={10} /> Debit</span>
                    )}
                  </td>
                  <td className="p-3 font-mono text-[10px]">{t.service_key || t.reason || "—"}</td>
                  <td className={`p-3 text-right ${t.type === "credit" ? "text-green-700" : "text-red-700"}`}>
                    {t.type === "credit" ? "+" : "−"}₹{t.amount.toFixed(2)}
                  </td>
                  <td className="p-3 text-right">₹{t.balance_after.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
